/**
 * -----------------------------------------------------------------------------
 * NCJ COMPONENT: VIN Check Webhook Integration
 * FILE: ncj-vin-check.js
 * PURPOSE: Handles VIN input, N8N webhook call, and Section Rendering API fetch.
 * AUTHOR: No Code Jungle (NCJ)
 * -----------------------------------------------------------------------------
 */

function getVinErrorMessage(data) {
  const translations = window.vinCheckTranslations?.errorTypes || {};

  if (data.message === "Invalid reCAPTCHA") {
    return translations.recaptcha || translations.generic;
  }

  if (data.message === "unauthorized request") {
    return translations.invalidWorkflow || translations.generic;
  }

  if (data.message === "Too many requests from this IP") {
    return translations.blockedIp || translations.generic;
  }

  if (data.message === "COC not available" || data.message === "We can't provide this COC.") {
    return translations.notAvailable || translations.generic;
  }

  if (data.message === "Error VIN" || data.manufacturer === "unknown") {
    return translations.unknownManufacturerOrErrorVin || translations.generic;
  }

  return translations.generic;
}

document.addEventListener("DOMContentLoaded", function () {
  const input = document.getElementById("vin-input");
  const button = document.getElementById("verify-button");
  const successBlock = document.getElementById("vin-success");
  const errorBlock = document.getElementById("vin-error");
  const successMessage = document.getElementById("vin-success-message");
  const container = document.getElementById("vin-product-content");
  const spinner = document.getElementById("verify-spinner");
  const charCounter = document.getElementById("vin-char-counter");
  const locale = window.shopifyLocale || "unknown"; // Fallback en caso de que no esté definido
  const lengthError = document.getElementById("vin-length-error");

  const API_ENDPOINT = "https://ftc.app.n8n.cloud/webhook/verify-vin";
  const SITE_KEY = window.recaptchaSiteKey;

  if (!input || !button) return;

  // ✅ Ejecutar verificación al presionar Enter
  input.addEventListener("keydown", function (event) {
    if (event.key === "Enter") {
      event.preventDefault();
      button.click();
    }
  });

  // 🆕 Lógica de normalización y contador en tiempo real
  input.addEventListener("input", () => {
    let rawValue = input.value;

    rawValue = (window.NCJUtils && window.NCJUtils.normalizeVin) ? window.NCJUtils.normalizeVin(rawValue) : rawValue.toUpperCase().replace(/[^A-Z0-9]/g, "").replace(/I/g, "1").replace(/[OQ]/g, "0").slice(0, 17);
    input.value = rawValue;

    const length = rawValue.length;

    if (charCounter) {
      charCounter.textContent = `${length}/17`;
      if (length < 17) {
        charCounter.classList.add("invalid-length");
      } else {
        charCounter.classList.remove("invalid-length");
      }
    }

    // Ocultar mensaje de error en cuanto empiece a corregir
    input.classList.remove("error");
    lengthError.textContent = "";
  });

  button.addEventListener("click", async function (e) {
    e.preventDefault();

    // Reset
    successBlock.style.display = "none";
    errorBlock.style.display = "none";
    successMessage.textContent = "";
    container.textContent = "";
    spinner.style.visibility = "visible"; // Mostrar spinner

    const vinInput = input.value.trim();

    // Validar longitud
    if (!vinInput || vinInput.length !== 17) {
      input.classList.add("error");
      charCounter.classList.add("invalid-length");
      spinner.style.visibility = "hidden";
      errorBlock.style.display = "none"; // ocultar error general

      lengthError.textContent = window.vinCheckTranslations.min17Char;

      return;
    } else {
      input.classList.remove("error");
      charCounter.classList.remove("invalid-length");

      lengthError.textContent = "";
    }

    try {
      // Ejecutar reCAPTCHA v3 (wrapped in ready() — reCAPTCHA may still be loading due to deferred init)
      const token = await new Promise((resolve, reject) => {
        if (typeof grecaptcha === 'undefined') { reject(new Error('reCAPTCHA not loaded')); return; }
        grecaptcha.ready(function() {
          grecaptcha.execute(SITE_KEY, { action: "verify_vin" }).then(resolve).catch(reject);
        });
      });

      const rayId = 'w1YZ^404CLE3oFAs3PDL87PI2ci'; // tu ID rastreo opcional

      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'cocworkflow': 'z@ZzuqLegLYC9&U^3*zL5iS6oaZB'
        },
        body: JSON.stringify({
          vin: vinInput,
          rayId: rayId,
          recaptchaToken: token,
          locale: locale,
          urlUsed: window.location.href
        })
      });

      let json = await response.json();

      // Normalizar respuesta anidada de n8n (producción)
      if (Array.isArray(json) && json.length > 0 && json[0].details && typeof json[0].details.output === 'string') {
        try {
          json = JSON.parse(json[0].details.output);
        } catch (parse_err) {
          console.error("Error al parsear el output anidado de n8n:", parse_err);
        }
      }

      if (json.valid === "true" || json.valid === "pending_verification") {
        const productHandle = json.productHandle;

        // Fetch HTML via Section Rendering API
        try {
          const currentLocalePrefix = window.location.pathname.match(/^\/(bg|cs|da|de|el|en|es|fr|it|nl|pl|pt|pt-pt|ro)(?=\/|$)/i)?.[0] || "";
          const res = await fetch(`${currentLocalePrefix}/products/${productHandle}?section_id=ncj-vin-check-results`);
          if (!res.ok) {
            console.error(`[VIN Check] Failed to fetch section HTML. Status: ${res.status}`);
            throw new Error(`Failed to fetch section HTML. Status: ${res.status}`);
          }
          const html = await res.text();

          // Inyectamos el HTML pero mantenemos el contenedor oculto (display: none via CSS styles in index)
          container.innerHTML = html;

          if (typeof initVinCheckResults === "function") {
            initVinCheckResults(json, container);
          } else {
            console.warn("initVinCheckResults not found");
          }

          // Esperar a que la imagen principal cargue antes de renderizar para evitar salto visual
          const img = container.querySelector('.vin-product-image img');
          if (img && !img.complete) {
            await new Promise((resolve) => {
              const timeout = setTimeout(resolve, 3000); // 3 seconds timeout max
              img.onload = () => { clearTimeout(timeout); resolve(); };
              img.onerror = () => { clearTimeout(timeout); resolve(); };
            });
          }

          // Ahora sí que mostramos el contenedor de golpe, con imagen incluida
          document.getElementById("vin-product").style.display = "block";

          // Lanzamos un evento global resize para forzar el match del ancho del toggle 'UPS' con las variantes
          window.dispatchEvent(new Event('resize'));

        } catch (fetchErr) {
          console.error("Error fetching rendered section:", fetchErr);
          errorBlock.style.display = "block";
        }
      } else {
        const errorMessage = getVinErrorMessage(json);
        const errorTextElement = document.getElementById("vin-error-message");
        if (errorTextElement) {
          errorTextElement.textContent = errorMessage;
        }
        errorBlock.style.display = "block";
      }

    } catch (err) {
      console.error("Error al verificar VIN:", err);
      errorBlock.style.display = "block";
    }

    spinner.style.visibility = "hidden";
  });
});
